<template>
    <div
        class="dd-skeleton"
        :class="`dd-skeleton--${type}`"
        role="status"
        aria-busy="true"
    >
        <template v-if="type === 'table'">
            <div class="dd-skeleton__row dd-skeleton__row--head">
                <span
                    v-for="col in columns"
                    :key="`h${col}`"
                    class="dd-skeleton__bone dd-skeleton__bone--head"
                ></span>
            </div>
            <div v-for="row in rows" :key="row" class="dd-skeleton__row">
                <span
                    v-for="col in columns"
                    :key="`c${row}-${col}`"
                    class="dd-skeleton__bone"
                    :style="{ width: `${cellWidth(row, col)}%` }"
                ></span>
            </div>
        </template>

        <template v-else-if="type === 'cards'">
            <div v-for="row in rows" :key="row" class="dd-skeleton__card">
                <div class="dd-skeleton__card-main">
                    <span
                        class="dd-skeleton__bone dd-skeleton__bone--card-title"
                        :style="{ width: `${cellWidth(row, 1) / 2}%` }"
                    ></span>
                    <span
                        v-for="line in 3"
                        :key="line"
                        class="dd-skeleton__bone dd-skeleton__bone--card-line"
                        :style="{ width: `${cellWidth(row, line) / 3}%` }"
                    ></span>
                </div>
                <span
                    class="dd-skeleton__bone dd-skeleton__bone--card-side"
                ></span>
            </div>
        </template>

        <template v-else>
            <span class="dd-skeleton__bone dd-skeleton__bone--title"></span>
            <div class="dd-skeleton__grid">
                <div
                    v-for="field in rows"
                    :key="field"
                    class="dd-skeleton__field"
                >
                    <span
                        class="dd-skeleton__bone dd-skeleton__bone--label"
                    ></span>
                    <span
                        class="dd-skeleton__bone dd-skeleton__bone--input"
                    ></span>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
// DoorDash-style loading placeholder (tasks#211): #f1f1f1 bones with a soft shimmer, shown while a
// list or an edit form loads for the first time.
export default {
    props: {
        type: {
            type: String,
            default: 'table',
            validator: (type) => ['table', 'form', 'cards'].includes(type),
        },
        rows: {
            type: Number,
            default: 8,
        },
        columns: {
            type: Number,
            default: 6,
        },
    },
    methods: {
        // a stable, uneven rhythm so the bones do not look like a grid of equal bars
        cellWidth(row, col) {
            return 45 + ((row * 7 + col * 13) % 5) * 10
        },
    },
}
</script>

<style lang="scss">
$skeleton-bone: #f1f1f1;
$skeleton-shine: #f7f7f7;

.dd-skeleton {
    &__bone {
        display: block;
        height: 16px;
        border-radius: 8px;
        background: linear-gradient(
                90deg,
                $skeleton-bone 0%,
                $skeleton-shine 50%,
                $skeleton-bone 100%
            )
            0 0 / 200% 100%;
        animation: dd-skeleton-shimmer 1.4s ease-in-out infinite;

        @media (prefers-reduced-motion: reduce) {
            animation: none;
        }
    }

    &--table {
        overflow: hidden;
    }

    &__row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
        grid-auto-flow: column;
        gap: 32px;
        align-items: center;
        min-height: 48px;
        padding: 0 16px;
        border-bottom: 1px solid #e4e4e4;

        &--head {
            border-bottom-width: 2px;
        }

        &--head .dd-skeleton__bone {
            width: 60%;
            height: 12px;
        }

        // phones: two readable columns instead of six dots
        @media (max-width: 599px) {
            > :nth-child(n + 3) {
                display: none;
            }
        }
    }

    &__bone--title {
        width: 30%;
        max-width: 240px;
        height: 24px;
        margin-bottom: 24px;
    }

    // card lists (company allowances): the DoorDash Teams budget cards
    &__card {
        display: flex;
        gap: 16px;
        padding: 16px 24px;
        margin-bottom: 16px;
        border: 1px solid #e7e7e7;
        border-radius: 12px;
    }

    &__card-main {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__bone--card-title {
        height: 20px;
        margin-bottom: 8px;
    }

    &__bone--card-line {
        height: 12px;
    }

    &__bone--card-side {
        flex: 0 0 96px;
        height: 16px;
    }

    &--form {
        padding: 24px;
        border: 1px solid #e4e4e4;
        border-radius: 12px;
    }

    &__grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 24px;
    }

    &__bone--label {
        width: 40%;
        height: 14px;
        margin-bottom: 8px;
    }

    &__bone--input {
        width: 100%;
        height: 40px;
    }
}

@keyframes dd-skeleton-shimmer {
    from {
        background-position: 100% 0;
    }

    to {
        background-position: -100% 0;
    }
}
</style>
